import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import {
  isRealCaseId,
  toCaseDetail,
  toCaseRecord,
  toCaseSummary,
  toLegDetail,
  toWireStatus,
  type CaseDetailWire,
  type LegDetailWire,
  type CaseRecord,
  type CaseWire,
} from '@/features/cases/api/caseWire'
import type { CaseDraft } from '@/features/cases/components/CaseFormSheet'
import type {
  ExpenseDraft,
  LegDraft,
} from '@/features/cases/components/CaseRecordSheets'
import { MOCK_CASE_DETAIL } from '@/mocks/cases'
import { MOCK_CASES, USE_MOCKS } from '@/mocks/fixtures'
import { normaliseDecimal } from '@/features/finance/lines'
import { findCountry } from '@/lib/countries'
import { isSuppressed } from '@/types/domain'
import type {
  CaseDetail,
  CaseLeg,
  CaseTeamMember,
  CasePayload,
  CaseStatus,
  CaseSummary,
  ExpenseLine,
  LegPayload,
  LegUnitPayload,
  Maybe,
} from '@/types/domain'

/** `enabled` for callers that only need the list conditionally — global
 *  search, which must not pull every case on a page load that never opens it. */
export function useCases({ enabled }: { enabled?: boolean } = {}) {
  return useQuery<CaseSummary[]>({
    queryKey: queryKeys.cases.list(),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_CASES
        : (await api.get<{ items: CaseWire[] }>('/cases')).items.map(toCaseSummary),
  })
}

/** The case record.
 *
 *  `enabled` on a non-empty id for the reason `useCaseLeg` gives: a route
 *  param that has not resolved is not an empty case, and firing `/cases/`
 *  would ask the server a question about nothing and render its answer as a
 *  failure on a page that is merely still routing.
 *
 *  The `enabled` option is `useCases`' — for a caller that needs the case only
 *  conditionally: the carrier payment sheet reads the case's issuing entity
 *  while it is open, and a closed sheet on every truck row must not fetch. */
export function useCase(caseId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<CaseDetail>({
    queryKey: queryKeys.cases.detail(caseId),
    enabled: enabled && caseId !== '',
    queryFn: async () => {
      if (!USE_MOCKS) {
        // The same dispatch the server's composer makes (readmodel_case.go): a
        // uuid is the real module's record and arrives in its wire spelling; a
        // fixture id arrives already speaking the domain vocabulary.
        if (isRealCaseId(caseId)) {
          return toCaseDetail(await api.get<CaseDetailWire>(`/cases/${caseId}`))
        }
        return api.get<CaseDetail>(`/cases/${caseId}`)
      }

      const record = MOCK_CASE_DETAIL[caseId]
      if (!record) {
        // Mirror the API: 404 for both missing and not-owned, so the fixture
        // path exercises the same error rendering as the real one.
        return Promise.reject(
          new ApiError({
            type: 'https://asagateway.com/problems/not-found',
            title: 'Not found',
            status: 404,
          }),
        )
      }
      return Promise.resolve(record)
    },
  })
}

/** The sheet's draft as the server takes it (POST/PUT /cases).
 *
 * Here rather than in each page that submits one, so the two entries into the
 * sheet — the case list and the dashboard — cannot disagree about what a case
 * create sends. (It lives beside the mutation rather than beside the sheet
 * because a component file may export only components.)
 *
 * The sheet's two route ends map to the OUTER pair of the four-part route: the
 * crossing and the unloading place belong to the legs, which are added on the
 * case itself. Blank optionals are dropped rather than sent as "" — the server
 * reads an absent field as "nobody stated this", and so should the wire.
 *
 * `openedOn` is deliberately NOT sent. It is the day the case was OPENED, which
 * is not the day the cargo departs, and the case header has no ETD to put a
 * departure in — departures belong to the legs (FR-24). The server stamps
 * today, which is what opening a case now means. */
export function toCasePayload(draft: CaseDraft): CasePayload {
  return {
    clientId: draft.clientId,
    placeOfLoading: draft.origin.trim() || undefined,
    placeOfDischarge: draft.destination.trim() || undefined,
    transportModeId: draft.transportModeId || undefined,
    eta: draft.eta || undefined,
    /* Sent ALWAYS, even as false, and carried on the draft rather than defaulted
       here. PUT /cases/:id treats this body as the whole record — an absent key
       is read as "not received" and clears the flag conversion copied from the
       quote (backend/internal/casefile/handlers.go says so at `caseBody`). Today
       only the create path submits this payload and false is the truth for a
       case opened by hand; the moment an edit sheet reuses it, the field's
       presence on CaseDraft is what forces that sheet to prefill the case's own
       value instead of quietly answering "no" for it. */
    finalPackingListReceived: draft.finalPackingListReceived,
    // Not dropped when blank, unlike the optionals above: the server requires
    // both (FR-70, FR-32), and the sheet will not submit without them. Sent as
    // whatever the draft holds rather than defaulted here — an empty required
    // select is honest, and a default picked on this side would put terms on a
    // case that nobody chose.
    paymentTermsKind: draft.paymentTermsKind as CasePayload['paymentTermsKind'],
    paymentMethod: draft.paymentMethod as CasePayload['paymentMethod'],
    /* The day count travels for `credit` and for nothing else — the server
       refuses a number on a kind that has no days, because a stray one would be
       read later as a due date the client never agreed to. Parsed here rather
       than sent as text: the field is a number on the wire. */
    paymentTermsCreditDays:
      draft.paymentTermsKind === 'credit' && draft.paymentTermsCreditDays.trim()
        ? Number(draft.paymentTermsCreditDays)
        : undefined,
  }
}

/** Opens a case with no quote behind it (FR-70).
 *
 * Not optimistic, for the reason the quote header is not: a case is NUMBERED by
 * the server (AD-12), and a number is not something the client can guess and
 * then correct. The sheet stays open on `pending` until the server answers with
 * the real record — and stays open holding the draft if it refuses, so nothing
 * typed is lost to a failed save (UX-DR-18). */
export function useCreateCase() {
  const queryClient = useQueryClient()
  return useMutation<CaseSummary, Error, CasePayload>({
    mutationFn: async (payload) =>
      toCaseSummary(await api.post<CaseWire>('/cases', payload)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.all })
    },
  })
}

/* ── Lifecycle (FR-25, AD-24) ─────────────────────────────────────────────── */

/** Every legal move, keyed by the state being left — the display spelling of
 *  the table in backend/internal/casefile/domain.go.
 *
 *  A COPY of a server rule, which normally earns a hard look. It is here for
 *  what AD-24 asks of a surface: the board must know, without a round trip,
 *  which lanes may light up under a dragged card. The server still decides —
 *  every move it permits here is re-checked by `Status.CanTransitionTo` and
 *  refused if this table is wrong or stale (AD-21). So the cost of drift is a
 *  lane that looks droppable and then reports a refusal, never a move the
 *  server did not sanction.
 *
 *  Note what is absent: nothing leaves financially-closed or cancelled. Both
 *  are terminal, and reopening a financially closed case is an audited
 *  Super-admin action (FR-25, FR-37) that deliberately is not a drag. */
const CASE_TRANSITIONS: Record<CaseStatus, readonly CaseStatus[]> = {
  draft: ['in-progress', 'cancelled'],
  /* → draft is the correction, and the ONLY way into that lane: a case is
     created in-progress (0007's column default), so draft is not where a case
     starts but where one goes that was opened too early. */
  'in-progress': ['draft', 'stuck', 'delivered', 'operationally-closed', 'cancelled'],
  stuck: ['in-progress', 'delivered', 'operationally-closed', 'cancelled'],
  delivered: ['operationally-closed', 'in-progress', 'cancelled'],
  'operationally-closed': ['financially-closed', 'in-progress', 'cancelled'],
  'financially-closed': [],
  cancelled: [],
}

/** Whether the move from one state to another is a legal SHAPE.
 *
 *  Shape only, exactly as the server's namesake means it: a true here does not
 *  mean the move will be accepted. The financial close additionally requires
 *  every balance settled, which only finance can answer (AD-3) — so the board
 *  offers the lane and the server refuses the unsettled case, with a reason. */
export function canTransitionCase(from: CaseStatus, to: CaseStatus): boolean {
  return CASE_TRANSITIONS[from].includes(to)
}

/** Moves a case between lifecycle states (FR-25, AD-24).
 *
 *  OPTIMISTIC, and it is the one write on a case that has earned it. A drag is
 *  a direct-manipulation gesture: the card must stay where it was dropped, or
 *  the hand that moved it has to wait for the network to agree that it moved.
 *  Nothing here is money — this is where the shipment IS — and the rollback is
 *  honest, because a refused move puts the card back in the lane it came from,
 *  which is exactly what "that move was not allowed" looks like.
 *
 *  Writes the list cache directly rather than invalidating it on the way in: an
 *  invalidate would refetch the pre-move list and snap the card back for as
 *  long as the round trip takes, which is the flicker this exists to avoid.
 *  The invalidate happens on settle, once the server's answer is the truth. */
export function useTransitionCase() {
  const queryClient = useQueryClient()

  return useMutation<
    CaseSummary,
    Error,
    { caseId: string; status: CaseStatus },
    { previous?: CaseSummary[] }
  >({
    mutationFn: async ({ caseId, status }) => {
      if (USE_MOCKS) {
        const found = MOCK_CASES.find((row) => row.id === caseId)
        return { ...(found as CaseSummary), status }
      }
      return toCaseSummary(
        await api.post<CaseWire>(`/cases/${caseId}/status`, {
          status: toWireStatus(status),
        }),
      )
    },

    onMutate: async ({ caseId, status }) => {
      const key = queryKeys.cases.list()
      // In flight refetches are cancelled first: one that resolves after this
      // write would overwrite the moved card with the server's older copy.
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<CaseSummary[]>(key)

      queryClient.setQueryData<CaseSummary[]>(key, (current) =>
        current?.map((row) => (row.id === caseId ? { ...row, status } : row)),
      )

      return { previous }
    },

    // The card returns to the lane it was dragged out of. Restoring the whole
    // previous list rather than reversing the one field, so a move that raced
    // another change does not leave a half-rolled-back list behind.
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.cases.list(), context.previous)
      }
    },

    /* Both readers, named separately.
     *
     * `cases.all` is the prefix of every case key, so it already reaches the
     * detail — but the detail is now a CALLER as well as a reader (the record
     * screen's lifecycle controls post through this same mutation), and what
     * it re-reads is not only the status: `nextStatuses` comes back changed by
     * the very move that just landed, and a stale copy is a menu offering a
     * transition the case can no longer make. Naming the detail key here is
     * what keeps that true if `cases.all` is ever narrowed to the list. */
    onSettled: (_data, _error, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    },
  })
}

/** Reopens a financially closed case (FR-25, FR-37).
 *
 *  Its OWN endpoint rather than a value handed to `useTransitionCase`, and the
 *  separation is the whole point (AD-24): financially-closed is terminal in the
 *  transition table above, so nothing that drags a card or presses a lane can
 *  arrive here by accident. Reopening is a deliberate, audited Super-admin
 *  action, and the reason travels with it because an audit entry without one is
 *  a weaker record than an audit entry with (FR-37).
 *
 *  It moves the case back to the OPERATIONAL close, never to in-progress:
 *  reopening undoes the FINANCIAL close, it does not re-litigate the delivery.
 *
 *  Never optimistic, unlike the drag above. Nothing here is a direct-
 *  manipulation gesture that must keep up with a hand, and the server writes an
 *  audit entry in the same transaction as the status — a screen showing the
 *  case reopened before that committed would be claiming a log entry that
 *  nobody wrote.
 *
 *  The answer is MERGED into the detail rather than replacing it: a lifecycle
 *  endpoint returns the record without the sections the read model composes
 *  around it (see toCaseRecord), so a wholesale write would blank the route,
 *  the parties and the money until the next fetch. The list is invalidated
 *  instead of patched — it is another screen, and it can afford a round trip. */
export function useReopenCase(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<CaseRecord, Error, string>({
    mutationFn: async (reason) => {
      if (USE_MOCKS) {
        const found = MOCK_CASES.find((row) => row.id === caseId)
        return {
          ...(found as CaseSummary),
          status: 'operationally-closed',
          // From the same table the board reads, rather than a hand-written
          // list: a fixture that disagreed with the transition rules would
          // train the offline surface to offer a move the server refuses.
          nextStatuses: [...CASE_TRANSITIONS['operationally-closed']],
        }
      }
      return toCaseRecord(
        await api.post<CaseWire>(`/cases/${caseId}/reopen`, { reason }),
      )
    },

    onSuccess: (record) => {
      queryClient.setQueryData<CaseDetail>(
        queryKeys.cases.detail(caseId),
        (current) => (current ? { ...current, ...record } : current),
      )
      /* The merge above lands the new status at once, but the response is
         casefile's record and carries no money — and the case's headline
         profit is chosen BY the status (S-6): «итог» while financially
         closed, «прогноз» once reopened. Refetch the composed detail so the
         money line and the header stop showing the closed case's cash. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
    },
  })
}

/* ── Legs (Story 3.4) ─────────────────────────────────────────────────────── */

/** A suppressed value never loads into an editable input (AD-6): a form that
 *  seeded the marker would write it back as if somebody had typed it. */
function editable(value: Maybe<string> | undefined): string {
  return value === undefined || isSuppressed(value) ? '' : value
}

/** The leg's carrier as an editable pair — what the leg form seeds and what a
 *  new unit on the leg page's unit cards is born with (QW-8). One rule for both: the
 *  server sends `""` for an unlinked carrier and a leg may carry a name with no
 *  id, and a withheld identity is blank here, never the marker. */
export function legCarrierOf(record: CaseLeg): { carrierId: string; carrierName: string } {
  return {
    carrierId: editable(record.carrierId),
    carrierName: editable(record.carrierName),
  }
}

/** The draft a saved leg opens as — the inverse of `toLegPayload` below.
 *
 *  Here rather than in the sheet, because there are now TWO surfaces that edit
 *  a leg: the create sheet on the case's section and the card on the leg's own
 *  page (issue #35 story 3). A seed written twice is two answers to "which
 *  fields does editing a leg carry", and the page's copy is the one that would
 *  have drifted — it is the surface that asks for the actuals, and a seed that
 *  forgot one of them would silently CLEAR it on the next save, because the PUT
 *  replaces the whole record.
 *
 *  THE SAME TRAP CLOSED ONCE ON THE TRUCKS, and it is worth naming because the
 *  shape that produced it looked like a simplification. This seed flattened each
 *  vehicle to its plate and `toLegPayload` sent `[{plate}]` back; the server's
 *  vehicle half upserts by plate and UPDATEs every column of a surviving row, so
 *  correcting an ETA on the page erased the trailer, the container, the CMR
 *  number, the haulier, the driver, the telephone and the weight of every truck
 *  on the direction — the whole of story 6's own feature, under a save that
 *  reported success. The draft carried the WHOLE row after that; since S-18b it
 *  carries NO units at all. The units are written only from the leg page's
 *  unit cards (`UnitsCard`), whole, beside this draft and with their version
 *  (`LegUnitsWrite`), so a payload built from this seed has no units key — and
 *  an absent key KEEPS the stored units on the PUT.
 *
 *  It lives beside `toLegPayload` rather than beside the form for the reason
 *  that function gives: the round trip record → draft → payload has to agree
 *  with itself, and both halves of it are here. */
export function legDraftFrom(record: CaseLeg): LegDraft {
  return {
    origin: record.origin,
    destination: record.destination,
    originCountry: record.originCountry ?? '',
    destinationCountry: record.destinationCountry ?? '',
    /* The catalogue id the server already serves, taken as given.
     *
     * It used to be reconstructed in the sheet —
     * `modes.find((mode) => mode.code === record.mode)` — and that lookup could
     * not succeed: `record.mode` is the LOWERCASE display mode the wire mapper
     * produces, `mode.code` is the UPPERCASE catalogue code, and the display
     * mode is lossy besides (every unrecognised code folds into 'multimodal').
     * So reopening a leg showed an empty mode field, and re-saving it after a
     * status change cleared the mode on the record. It also raced the directory
     * query: on the first paint `modes` is undefined, and the seed runs exactly
     * once.
     *
     * Nothing is derived now, which is why none of that can come back. */
    transportModeId: record.transportModeId,
    status: record.status,
    transshipment: record.transshipment,
    ...legCarrierOf(record),
    reference: record.reference ?? '',
    voyageNumber: record.voyageNumber ?? '',
    /* No units here (S-18b): they travel beside the draft, never inside it —
       see the comment above. */
    etd: record.etd ?? '',
    eta: record.eta ?? '',
    atd: record.atd ?? '',
    ata: record.ata ?? '',
    stallNote: record.stallNote ?? '',
    lastLocation: record.lastLocation ?? '',
  }
}

/** A leg nobody has answered anything about yet.
 *
 *  A FUNCTION rather than a shared constant, and here rather than beside the
 *  form for `legDraftFrom`'s reason: the create surface and the round trip
 *  record → draft → payload have to agree about what a leg is made of, and both
 *  halves of that agreement live in this file. (A component module may export
 *  only components — `react-refresh/only-export-components` — which is the other
 *  reason it cannot sit beside `LegFields`.) */
export function emptyLegDraft(): LegDraft {
  return {
    // The ends and the mode are blank HERE, and only here. The create surface
    // does not open on this draft any more: it opens on `legDraftForCase`,
    // which seeds these five from the case and the previous leg (QW-8). This
    // stays the blank leg — what the seed starts from, and what the tests build
    // on — and makes no claim about what a new leg should show.
    origin: '',
    destination: '',
    originCountry: '',
    destinationCountry: '',
    transportModeId: '',
    status: 'planned',
    transshipment: false,
    carrierId: '',
    carrierName: '',
    reference: '',
    voyageNumber: '',
    // No units: the leg form has no unit control (S-8, S-18b) — trucks,
    // containers and every other unit are entered on the leg page's unit
    // cards, which send them beside this draft.
    etd: '',
    eta: '',
    // The four actuals are ABSENT rather than empty, which says the true thing:
    // a leg being entered has not departed, has not arrived, is not standing
    // still anywhere and has never been reported from a place. The page that
    // edits a running leg is where those questions belong.
  }
}

/** One end of a leg as the create form seeds it: a stored country CODE becomes
 *  the country with no place, and anything else stays the place, beside the
 *  country stored with it (or none). The route columns hold two generations —
 *  "KZ" from the picker and "Ashgabat" from before it — and a code typed into
 *  the place box would print as a town called "KZ". */
function seededEnd(
  place: string | undefined,
  country?: string,
): { place: string; country: string } {
  const code = findCountry(place)?.code
  if (code) return { place: '', country: code }
  return { place: place ?? '', country: country ?? '' }
}

/** Whether two seeded ends are the same place. Two PLACES are compared as text,
 *  trimmed and case-folded, and the countries are ignored: the previous leg
 *  names "Ashgabat" with TM beside it while the case's column is the bare
 *  text, and the two are one end. Two place-less ends are compared by their
 *  country codes. A place against a country alone is not decidable, and is
 *  left to the reader. */
function sameEnd(
  a: { place: string; country: string },
  b: { place: string; country: string },
): boolean {
  const fold = (place: string) => place.trim().toLocaleLowerCase()
  if (fold(a.place) !== '' && fold(b.place) !== '') return fold(a.place) === fold(b.place)
  if (fold(a.place) === '' && fold(b.place) === '') {
    return a.country !== '' && a.country === b.country
  }
  return false
}

/** The draft a NEW leg on this case opens with (QW-8, D-2).
 *
 *  This reverses what `emptyLegDraft` used to stand for — a leg's ends and mode
 *  were never guessed — on the owner's call, because the desk was retyping on
 *  every leg what the case and the previous leg already said. Each guess is
 *  still made narrowly, and every one of them is a visible, editable value:
 *
 *  - the ORIGIN is where the latest leg ends — the highest `sequence`, never
 *    the last row in array order — or the case's own origin on a case with no
 *    legs yet;
 *  - the DESTINATION is the case's, and blank when it is the same end as that
 *    origin (`sameEnd`: the same place ignoring case, spaces and countries, or
 *    the same country for two place-less ends): a converted CN→TM case whose
 *    one leg already reaches TM would otherwise offer a TM→TM leg, and one
 *    whose leg reaches "Ashgabat, TM" an Ashgabat → Ashgabat one;
 *  - the MODE is the case's catalogue row, and blank when the case is
 *    multimodal or names none: one leg is one movement, and a multimodal id on
 *    it would say nothing about which.
 *
 *  A direction template still overwrites the two ends when one is picked
 *  (the leg page's «Маршрут» tab, S-10: the chain's first and last point
 *  are the ends). */
export function legDraftForCase(record: CaseDetail): LegDraft {
  const latest = [...(record.legs ?? [])].sort((a, b) => b.sequence - a.sequence)[0]
  const origin = latest
    ? seededEnd(latest.destination, latest.destinationCountry)
    : seededEnd(record.origin)
  const caseEnd = seededEnd(record.destination)
  const destination = sameEnd(caseEnd, origin) ? { place: '', country: '' } : caseEnd
  const singleMode =
    record.mode !== undefined && record.mode !== 'multimodal'
      ? (record.transportModeId ?? '')
      : ''

  return {
    ...emptyLegDraft(),
    origin: origin.place,
    originCountry: origin.country,
    destination: destination.place,
    destinationCountry: destination.country,
    transportModeId: singleMode,
  }
}

/** What a leg cannot be saved without: two ends and a mode.
 *
 *  Shared for the seed's reason — the page's form and the create sheet must
 *  refuse the same drafts, and a movement with no mode is not a movement on
 *  either surface. The server checks all three again (AD-21); this only spares
 *  the typist a round trip. */
export function legDraftIncomplete(draft: LegDraft): boolean {
  return !draft.origin.trim() || !draft.destination.trim() || !draft.transportModeId
}

/** The leg sheet's draft as the server takes it (POST/PUT /cases/:id/legs).
 *
 * Here rather than in the sheet for the same reason `toCasePayload` is: the
 * section adds legs from two places — the empty state and the section header —
 * and two call sites must not disagree about what a leg create sends.
 *
 * `sequence` is deliberately NOT sent. Where a leg sits in the route is the
 * server's to decide: a create appends and a delete renumbers, because two
 * forms submitting position 2 at once would leave the route with two second
 * legs and no first.
 *
 * Blank optionals are dropped rather than sent as "" — the server reads an
 * absent field as "nobody stated this", and so should the wire. */
export function toLegPayload(draft: LegDraft): LegPayload {
  return {
    origin: draft.origin.trim(),
    destination: draft.destination.trim(),
    originCountry: draft.originCountry.trim() || undefined,
    destinationCountry: draft.destinationCountry.trim() || undefined,
    transportModeId: draft.transportModeId,
    status: draft.status,
    transshipment: draft.transshipment,
    carrierId: draft.carrierId.trim() || undefined,
    /* Sent BESIDE the id rather than instead of it, and dropping it when a
       carrier is linked would be wrong twice over: a leg carried by a name off
       a waybill has no id and this is the only record of who moved it, and a
       legacy leg re-saved after an unrelated status change would come back with
       an empty carrier. The server resolves the pair on read — the linked
       directory carrier's name wins, this is the fallback. */
    carrierName: draft.carrierName.trim() || undefined,
    reference: draft.reference.trim() || undefined,
    voyageNumber: draft.voyageNumber.trim() || undefined,

    /* No units (FR-67, S-18b). The leg page's unit cards send the whole list
       beside this payload, with its version (`legUpdatePayload`'s `units`, the
       create's `units`), and every surface that does not edit the units sends
       NO units key — which the PUT reads as "keep the stored units". The
       retired `vehicles` / `containers` keys are never sent: the server
       answers 422 `retired` to a body that carries one.

       That is the opposite of the trap this payload used to carry: the
       server's vehicle half matched by plate and then UPDATEd the whole row,
       so a units list built from a stale or partial draft was an ERASURE
       rather than "leave it alone" — sending the plates alone is what wiped
       the trailers, the CMR numbers and the hauliers of a whole direction on
       any save from the leg page. A list that is never built cannot be
       built wrong. */

    etd: draft.etd || undefined,
    eta: draft.eta || undefined,

    /* The four the sheet never asked for, and the page does (issue #35 story
       3). They are optional on the draft rather than required because the
       CREATE surface still does not ask them — an actual departure on a leg
       that does not exist yet is not a fact anybody holds — and an absent key
       here means exactly what it means for every other field on this payload:
       nobody stated this, so clear it. That is what lets the page correct an
       ATD typed by mistake. */
    atd: draft.atd || undefined,
    ata: draft.ata || undefined,
    stallNote: draft.stallNote?.trim() || undefined,
    lastLocation: draft.lastLocation?.trim() || undefined,
  }
}

/** What the leg page's drawer changed: a DIFF against the leg as served, never
 *  a copy of it (S-10). A key present here is a field the reader set; a key
 *  absent is a field this page has no opinion about, and the PUT then carries
 *  the live record's value — or, for the three-state keys, nothing at all. The
 *  units are not here (and not on `LegDraft`, S-18b): they travel beside it,
 *  with their version. */
export type LegEdits = Partial<LegDraft>

/** The units a leg PUT carries — every kind, the whole list — and the token
 *  of the stored list they were edited from. One object so a caller cannot
 *  send the list without it: the server answers 422 `unitsVersion: required`
 *  to a PUT that does. */
export interface LegUnitsWrite {
  list: LegUnitPayload[]
  version: string
}

/** The leg PUT the leg page sends (S-10): the live record, the reader's changes
 *  over it, and NOTHING it did not change among the three-state keys.
 *
 *  `toLegPayload(legDraftFrom(live))` is the floor, so every whole-record key —
 *  the carrier, the dates, the stall note — goes back as the server holds it
 *  NOW unless the reader changed it. Over that floor:
 *
 *  - `status` and `lastLocation` are sent only when the reader changed them. The 15-minute sweep writes status and position; a desk that
 *    typed for twenty minutes would otherwise write the old ones back. An
 *    edited `lastLocation` brings `lastReportedAt`, stamped now — a position
 *    with the time it was reported, never one without (a blank location is a
 *    value too: it clears).
 *  - `units` goes only when the unit cards were touched, and always with
 *    `unitsVersion`: a stale version is refused (409 `leg-units-changed`)
 *    rather than reverting a colleague's units. The containers are units
 *    since S-18b, so the drawer never sends them.
 *  - On a leg with stored points the ENDS are never laid over: the chain owns
 *    them (`SetLegEnds` runs in its transaction), and the server keeps the
 *    stored ends whatever the body says. Sending the live ones says the same. */
export function legUpdatePayload({
  live,
  edits,
  units,
  now = () => new Date().toISOString(),
}: {
  live: CaseLeg
  edits: LegEdits
  units?: LegUnitsWrite
  now?: () => string
}): LegPayload {
  const changed: LegEdits = { ...edits }
  if ((live.points ?? []).length > 0) {
    delete changed.origin
    delete changed.originCountry
    delete changed.destination
    delete changed.destinationCountry
  }

  const merged = toLegPayload({ ...legDraftFrom(live), ...changed })
  /* The three-state keys leave the floor; each comes back below only on its
     own condition. The floor carries no units at all (`toLegPayload`). */
  const {
    status: _status,
    lastLocation: _lastLocation,
    ...whole
  } = merged
  const payload: LegPayload = whole

  if ('status' in changed) payload.status = merged.status
  if ('lastLocation' in changed) {
    payload.lastLocation = (changed.lastLocation ?? '').trim()
    payload.lastReportedAt = now()
  }
  if (units) {
    payload.units = units.list
    payload.unitsVersion = units.version
  }
  return payload
}

/** Adds a leg to a case's route (FR-24).
 *
 * Not optimistic, and the reason is the sequence: the server decides where the
 * leg lands in the route, so the client cannot render it in the right place
 * until the server answers. An optimistic leg would appear at the bottom and
 * then jump — or worse, sit at a position the server disagreed with.
 *
 * The sheet stays open on `pending` and stays open holding the draft if the
 * server refuses, so nothing typed is lost to a failed save (UX-DR-18). */
export function useAddLeg(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<CaseLeg, Error, LegPayload>({
    /* Mapped, not cast: the answer is the plain `casefile.LegDTO`, whose mode
       is the UPPERCASE catalogue code. The leg page reads the new unit ids
       and `unitsVersion` off this answer before its refetch lands (S-10),
       so the answer has to be the same shape the read is. */
    mutationFn: async (payload) =>
      toLegDetail(await api.post<LegDetailWire>(`/cases/${caseId}/legs`, payload)),
    onSuccess: () => {
      // The case detail carries the legs, so it is the query that must
      // refetch — invalidating a `legs` key alone would leave the section
      // rendering the detail's stale copy.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      // And the LIST, because a route change is now visible there too: the
      // board card reads the case's planned departure and the leg it is on off
      // the composed row (cmd/asa/readmodel_case_list.go). Invalidating the
      // detail alone was correct for exactly as long as legs stopped at the
      // record — a leg added from the route section then left the board card
      // naming the old position until something else happened to refetch.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      // A leg's carrier — and its units' — decides whose units and works a
      // counterparty's statement and the counterparties list count (S-22e).
      invalidateFinance(queryClient)
    },
  })
}

/** Amends one leg of a case's route.
 *
 *  Invalidates the list beside the detail for useAddLeg's reason, and this is
 *  the mutation that makes it matter most: reporting a leg in-transit or stuck
 *  at a border is exactly what moves the board card's position. */
export function useUpdateLeg(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<CaseLeg, Error, { legId: string; payload: LegPayload }>({
    // Mapped for `useAddLeg`'s reason.
    mutationFn: async ({ legId, payload }) =>
      toLegDetail(await api.put<LegDetailWire>(`/cases/${caseId}/legs/${legId}`, payload)),
    onSuccess: () => {
      /* This also refreshes the LEG PAGE, and it does so without naming it:
         `queryKeys.cases.leg(caseId, legId)` hangs under the detail key and
         invalidation matches by prefix. That is why the key was hung there —
         this mutation is fired from the page's own edit form AND from the case
         screen, and the case screen has never heard of the page. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      // The carrier and the units ride this PUT: useAddLeg's reason (S-22e).
      invalidateFinance(queryClient)
    },
  })
}

/** Removes a leg, and with it the gap it leaves in the route.
 *
 * The server renumbers the survivors, which is why this invalidates rather
 * than splicing the leg out of the cache: every leg after the deleted one has
 * a new sequence, and a client-side splice would leave the route reading
 * 1, 2, 4 until the next refetch. */
export function useRemoveLeg(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (legId) => api.delete<void>(`/cases/${caseId}/legs/${legId}`),
    onSuccess: (_void, legId) => {
      /* The deleted leg's own key is DROPPED rather than invalidated, and that
         is a different decision from the refresh below it — the same one
         `useDropInvoice` makes. The page showing this leg is about to navigate
         away, and a cached entry under a dead id would be handed to the next
         reader who reaches that URL before the refetch answered: a movement
         that no longer exists, rendered from memory. */
      queryClient.removeQueries({ queryKey: queryKeys.cases.leg(caseId, legId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      // The list carries the route too (useAddLeg), and a removal can change
      // the position as well as the total: dropping the leg the cargo was on
      // moves the card to the next one.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      // A removed leg takes its carrier's units off the counterparty's
      // statement: useAddLeg's reason (S-22e).
      invalidateFinance(queryClient)
    },
  })
}

/* The internal team (Story 3.6, FR-69).
 *
 * Its own query rather than a field read off the case detail, because the team
 * changes on its own schedule: assigning a colleague must re-render the header
 * without refetching the whole case, its legs, its files and its money. The
 * detail still carries `team` for the first paint — this keeps it fresh.
 */
export function useCaseTeam(caseId: string) {
  return useQuery<CaseTeamMember[]>({
    queryKey: queryKeys.cases.team(caseId),
    queryFn: async () =>
      (await api.get<{ items: CaseTeamMember[] }>(`/cases/${caseId}/team`)).items,
  })
}

/** Staffs a case (FR-69, FR-57).
 *
 * NOT optimistic. Assignment is what row scoping reads, so a colleague shown as
 * assigned before the server agreed is a screen claiming somebody can see a
 * shipment they cannot — and the two refusals this can meet (a deactivated
 * account, FR-57) are ones the user must actually be told about rather than
 * having a row appear and then vanish.
 *
 * The server answers with the WHOLE team, so nothing here splices a list. */
export function useAssignTeamMember(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<CaseTeamMember[], Error, string>({
    mutationFn: async (userId) =>
      (
        await api.post<{ items: CaseTeamMember[] }>(`/cases/${caseId}/team`, {
          userId,
        })
      ).items,
    onSuccess: (items) => {
      queryClient.setQueryData(queryKeys.cases.team(caseId), items)
      // The case detail carries the team for its first paint, so it must not
      // keep serving a stale copy beside the fresh one above.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    },
  })
}

/** Unstaffs a case (FR-69, FR-56).
 *
 * The refusal worth surfacing is the last assignee: a case with an empty team
 * is invisible to every non-founder, so the server refuses and the message
 * tells the user to assign somebody else first. */
export function useRemoveTeamMember(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<CaseTeamMember[], Error, string>({
    mutationFn: async (userId) =>
      (
        await api.delete<{ items: CaseTeamMember[] }>(
          `/cases/${caseId}/team/${userId}`,
        )
      ).items,
    onSuccess: (items) => {
      queryClient.setQueryData(queryKeys.cases.team(caseId), items)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    },
  })
}

/** What the send sheet submits. The recipient is deliberately NOT here: the
 *  server reads it off the case, so a payload cannot redirect a client's status
 *  to an address of its choosing, and the address the sheet displays is the one
 *  that is actually used. */
export interface StatusSendPayload {
  /** Optional. The status, route and ETA are the message; this is the sender's
   *  addition to them. */
  note: string
  /** The RECIPIENT's language, not the sender's — the client reads this. */
  language: 'en' | 'ru'
}

/** Sends the case's current status to the client (FR-41).
 *
 * Never optimistic, and it is the clearest case in the app for that: an email
 * leaves the system permanently, so "sent" must mean the server accepted it.
 * Showing it as sent and rolling back on failure would be telling someone their
 * client has been informed when nobody has.
 *
 * Invalidates the case on success so the send lands in the history and in the
 * send journal (S-15), and the bell count catches up — the team's record that
 * this went out is half of what the feature is for. The journal's key hangs
 * under the detail key, so the first invalidation already reaches it; it is
 * named as well so that moving the key cannot quietly leave the journal one
 * send behind. */
export function useSendCaseStatus(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ sentTo: string }, Error, StatusSendPayload>({
    mutationFn: async (payload) => {
      if (USE_MOCKS) return { sentTo: 'mock@example.com' }
      return api.post<{ sentTo: string }>(`/cases/${caseId}/status-update`, payload)
    },
    onSuccess: () => {
      if (!USE_MOCKS) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
        void queryClient.invalidateQueries({ queryKey: queryKeys.cases.statusSends(caseId) })
      }
    },
  })
}

/** What the bulk send submits from the case list. */
export interface BulkStatusSendPayload {
  clientId: string
  /** Empty means ALL of that client's cases — the sheet's "select all". One
   *  way to say it, so a flag cannot contradict a list somebody ticked. */
  caseIds: string[]
  note: string
  language: 'en' | 'ru'
}

/** One shipment named in a consolidated send's answer.
 *
 * `results[]` with a per-case `sent` flag is GONE, and the reason is the artefact
 * rather than tidiness (Story 5.8): the endpoint used to loop the per-case sender
 * and produce N emails, so one row could succeed while another failed. It now
 * sends ONE email in one transaction, which cannot half succeed — so a per-case
 * success flag would be a field with one possible value, and those are the fields
 * a reader trusts.
 *
 * What varies is which shipments the email CARRIES A ROW FOR and which were left
 * out, so that is the partition the server answers with. */
export interface BulkStatusCase {
  caseId: string
  caseNumber: string
}

/** A shipment left out of the email, with the reason.
 *
 * `reason` is a CODE, rendered client-side in the reader's language (AD-27).
 * `terminal` is a cancelled or financially closed shipment: a message whose
 * subject is where your cargo is has nothing to say about either, and including
 * one would tell a client their cancelled shipment is on its way.
 *
 * `no-contact` and `send-failed` are gone with the loop. The recipient is now
 * resolved once from the client, so "no address on file" is one condition for the
 * whole send and arrives as a 422 on `clientContactEmail`; and one email either
 * enqueues or the request fails. */
export interface BulkStatusSkip extends BulkStatusCase {
  reason: 'terminal'
}

export interface BulkStatusResponse {
  /** The one address the email was enqueued for. */
  sentTo: string
  /** How many shipments the email carries a row for. */
  sent: number
  /** How many were asked about — covered plus skipped. */
  total: number
  covered: BulkStatusCase[]
  skipped: BulkStatusSkip[]
}

/** Who a consolidated send would reach, for one client (FR-65, FR-47).
 *
 * Empty strings — not an absent object — are the ordinary answer for a client
 * with no contact on file, and the sheet renders that as the refusal that sends
 * somebody to the directory.
 */
export interface StatusRecipient {
  name: string
  email: string
}

/** Asks the server who a status update about this client's shipments would go
 *  to (FR-65).
 *
 *  A REQUEST rather than a field off a list row, and the difference is the
 *  defect this hook closes. The bulk sheet used to read `clientContactEmail`
 *  off the case summaries — a field `GET /cases` has never served, because
 *  casefile's CaseDTO does not carry one and only the composed DETAIL does. So
 *  the recipient was always undefined, and every client, however complete their
 *  directory record, was reported as having no contact email with the send
 *  disabled behind the notice.
 *
 *  Keyed by client because that is the shape of the question: one address
 *  serves the whole send. Disabled until a client is chosen, so opening the
 *  sheet costs nothing.
 *
 *  The answer comes from the same port, adapter and rule the SEND resolves
 *  through, which is why it is worth a round trip rather than a lookup in
 *  whatever the directory screen happens to have cached: the address shown here
 *  and the address the email leaves for cannot be two different answers. */
export function useStatusRecipient(clientId: string) {
  return useQuery<StatusRecipient>({
    queryKey: queryKeys.cases.statusRecipient(clientId),
    enabled: clientId !== '',
    queryFn: async () => {
      if (USE_MOCKS) {
        const row = MOCK_CASES.find((one) => one.clientId === clientId)
        return {
          name: row?.clientContactName ?? '',
          email: row?.clientContactEmail ?? '',
        }
      }
      return api.get<StatusRecipient>(
        `/cases/status-recipient?clientId=${encodeURIComponent(clientId)}`,
      )
    },
  })
}

/** Sends one client their status across several shipments in ONE email (FR-65).
 *
 * The list page's entry: pick a client, pick their shipments or all of them. Not
 * optimistic for the same reason the single send is not — an email cannot be
 * unsent, so "sent" must mean the server accepted it.
 *
 * It reaches internal/casefile now rather than internal/showcase, which is what
 * makes it work at all: the fixture behind this path filtered a fixture case list
 * by a fixture client id while this hook posts real uuids, so every send answered
 * 422 `caseIds: empty`.
 *
 * Invalidates every case rather than one: a consolidated send touches several,
 * and the team's record of what went out is half of what this feature is for.
 * `cases.all` is the prefix of every covered case's send journal (S-15: one
 * journal row per covered case), so each of them lists the send too. */
export function useSendClientStatuses() {
  const queryClient = useQueryClient()

  return useMutation<BulkStatusResponse, Error, BulkStatusSendPayload>({
    mutationFn: async (payload) => {
      if (USE_MOCKS) {
        return {
          sentTo: 'mock@example.com',
          sent: payload.caseIds.length,
          total: payload.caseIds.length,
          covered: payload.caseIds.map((caseId) => ({ caseId, caseNumber: caseId })),
          skipped: [],
        }
      }
      return api.post<BulkStatusResponse>('/cases/status-update', payload)
    },
    onSuccess: () => {
      if (!USE_MOCKS) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.cases.all })
      }
    },
  })
}

/* Chat used to live here (useSendChatMessage), posting to the showcase module's
 * /cases/:id/chat and reading the conversation off the case detail's `chat`
 * array. It is GONE (Story 8.3).
 *
 * Both halves moved to features/cases/api/useCaseChat, against the real
 * endpoints — GET/POST /cases/:id/messages — which page, poll and deduplicate
 * by client key. Keeping this beside that one would have meant two ways to send
 * a message with different guarantees, and the one a user reached would depend
 * on which component they happened to be in.
 *
 * The attachment half went with it. The showcase endpoint accepted objectKeys
 * and dropped them; the real chat schema (0023) has no attachment table, so a
 * composer that uploaded files and then posted a message which could not
 * reference them was uploading into a hole. Files on a case have their own
 * surface that does persist them (Story 8.1) — see useCaseFiles.
 */

/* ── Receivable tranches (Story 4.3) ──────────────────────────────────────── */

/** Moves one tranche's expected payment date, and nothing else (FR-33, AC 3).
 *
 * PATCH rather than PUT, matching the route: the request replaces ONE field. A
 * PUT would promise the whole representation and invite this hook to send back
 * an amount, which is the recomputation the acceptance criterion forbids and
 * which the server has no method to perform.
 *
 * `null` CLEARS the date, back to "no agreed date yet — not yet chaseable". That
 * is a real thing to want to say when a committed date falls through, and it is
 * why the argument is nullable rather than a required string.
 *
 * NOT optimistic. A date on a receivable is what aging chases and what a client
 * gets telephoned about, so it waits for the server (UX-DR-24) — and the case
 * detail carries the series, so the detail is what refetches. Writing the new
 * date into the cache first would show a commitment the server had not accepted.
 */
export function useSetTrancheDate(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    { trancheId: string; expectedOn: string | null }
  >({
    mutationFn: ({ trancheId, expectedOn }) =>
      api.patch<unknown>(`/cases/${caseId}/tranches/${trancheId}`, {
        expectedDate: expectedOn,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
    },
  })
}

/* ── Expense lines (Story 4.4) ─────────────────────────────────────────────── */

/** One FOLDED cost line as the server takes it (finance's foldedExpenseBody).
 *
 *  The server still stores planned and actual as distinct rows (AD-4) — one plan
 *  can be settled by two vendors' invoices, and `actualLines` exists to say so.
 *  What moved is WHO unfolds: the three endpoints behind this type do it inside
 *  one transaction, so this payload describes the line the way the table shows
 *  it, and a save that touches both sides is one request that either lands whole
 *  or does not land.
 *
 *  There is deliberately no `kind` and no `plansAgainst` here. They are what the
 *  split used to cost the client, and the price was not cheap: a field the client
 *  chooses is a field it can choose wrongly, and choosing `kind` wrongly is how
 *  one saved cost became two rows and half an edit went missing. The server
 *  derives both. */
export interface FoldedExpensePayload {
  item: string
  /** Absent for a cost with NO plan (S-4.1): the server books the actual alone,
   *  unplanned (`plans_against NULL`), and keeps it unplanned — a plan sent for
   *  such a line on a later PUT is refused (`planned: unplanned-line`). Never
   *  sent as `0.00` to mean "none": a zero is a figure, and the server refuses
   *  it as `planned: not-positive`. A planned line's PUT always carries it; the
   *  server answers its absence there with `planned: required`. */
  planned?: { amount: string; currency: string }
  /** Absent until the cost lands, which is the ordinary state of a planned line.
   *  Sent absent on a line that HAD one, the server deletes the actual row and
   *  leaves the plan standing.
   *
   *  Its currency is its OWN (`actual.currency`), not the plan's: a plan in USD
   *  is routinely invoiced in CNY. */
  actual?: { amount: string; currency: string }
  /** The counterparty's name as the draft shows it. Sent beside the id, but
   *  the server never trusts it for a new or changed link (S-22d): the name
   *  comes from the directory. */
  vendorName?: string
  /** The directory counterparty the actual is booked to (0018). REQUIRED with
   *  an actual since S-22d — 422 `vendorId: required` when absent, `invalid`
   *  for an id the directory does not hold — except on an amend that leaves
   *  an old name-only line exactly as stored. */
  vendorId?: string
  /** The actual's note, tri-state on the ACTUAL row only (S-22d): absent keeps
   *  the stored note; a string replaces it, trimmed, and `''` clears it. Sent
   *  only with an actual — with none it never touches a plan's note — and
   *  never when the reader may not see it. */
  remarks?: string
  /* No `settled`: whether a cost is paid is derived by the server from the
     payments allocated to it and served as the line's `settlement` (S-4.2).
     The server ignores the old field, so sending it would be a write that
     silently does nothing. */
}

/** The sheet's draft as ONE body.
 *
 *  The sheet shows one row with a planned figure and an optional actual, because
 *  that is what the money table reads — and now also what the write endpoints
 *  take. Nothing here decides which row a save is about; there is no longer a
 *  side to choose.
 *
 *  Each side goes in its own currency. There used to be one currency field, and
 *  it was stamped on BOTH sides — so editing a line planned in USD and invoiced
 *  in CNY silently re-booked the invoice in dollars.
 *
 *  A blank plan is sent ABSENT: that is a cost recorded with no plan, or an
 *  unplanned line being amended. Which of the two shapes the line has is the
 *  sheet's to enforce (a planned line keeps its plan required); this converter
 *  only refuses to invent a figure nobody typed.
 *
 *  The amount stays a decimal STRING throughout (AD-9), normalised only for the
 *  separators a Russian keyboard produces — never through Number(), which is how
 *  the cent goes. */
export function toFoldedExpensePayload(draft: ExpenseDraft): FoldedExpensePayload {
  const plannedAmount = draft.planned.trim()
  const body: FoldedExpensePayload = {
    item: draft.description.trim(),
    ...(plannedAmount === ''
      ? {}
      : {
          planned: {
            amount: normaliseDecimal(plannedAmount),
            currency: draft.plannedCurrency,
          },
        }),
  }

  const actual = draft.actual.trim()
  if (actual === '') {
    // No actual yet, which is the ordinary state of a planned line — the sheet
    // says so in as many words ("Оставьте пустым, пока затрата не понесена").
    // The vendor goes with it: it belongs to the actual, and the server refuses
    // a plan that carries one.
    return body
  }

  return {
    ...body,
    actual: {
      amount: normaliseDecimal(actual),
      // `null` is "nobody picked one": the actual follows the plan's currency.
      currency: draft.actualCurrency ?? draft.plannedCurrency,
    },
    // The vendor belongs to the ACTUAL and only to it (FR-20): a planned line
    // is vendor-less, and the server refuses one that is not.
    vendorName: draft.counterpartyName.trim() || undefined,
    // The link beside the name, and only ever beside it: the table's own
    // CHECK refuses an id with no name (0018,
    // `case_expense_linked_vendor_is_named`), so that retiring a counterparty
    // leaves the line still saying who was paid.
    vendorId: draft.vendorId ?? undefined,
    // The note rides the actual like the vendor does. `null` is a note this
    // reader may not see: ABSENT, so the stored one is kept — a save cannot
    // clear what its author cannot read (failure-modes: the blank that wipes
    // a hidden value). A string, '' included, is the reader's answer.
    ...(draft.note === null ? {} : { remarks: draft.note.trim() }),
  }
}

/** Records a cost line on a case (FR-30, Story 4.4).
 *
 *  NOT optimistic, and here the confirmed-write rule is squarely on point
 *  (UX-DR-24): this is money, and the row appears once the server has booked it.
 *  The server resolves the FX rate, computes the USD equivalent and refuses a
 *  currency it has no rate for — so an optimistic row would be showing a figure
 *  the client cannot actually compute, and would sometimes show one the server
 *  then rejects.
 *
 *  A draft carrying both a plan and an actual is ONE request. It used to be two
 *  posts, the second carrying the first's id as `plansAgainst`, and the second
 *  could fail after the first had already been written — for money, half a write
 *  nobody rolls back is worse than a refused one. The server now creates both
 *  rows and the AD-4 reference between them in one transaction.
 *
 *  The sheet stays open on `pending` and stays open holding the draft if the
 *  server refuses, so nothing typed is lost to a failed save (UX-DR-18). */
export function useRecordExpense(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<ExpenseLine, Error, ExpenseDraft>({
    mutationFn: (draft) =>
      api.post<ExpenseLine>(`/cases/${caseId}/expenses`, toFoldedExpensePayload(draft)),
    onSuccess: () => {
      // The case detail carries the money block, so it is the query that must
      // refetch — invalidating a `money` key alone would leave the section
      // rendering the detail's stale copy.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      // And the list, for the reason useAddLeg gives: the composed row carries
      // the forecast profit, and a cost recorded here is one of its two inputs.
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      invalidateFinance(queryClient)
    },
  })
}

/** The cross-case finance tree, refreshed after a cost line moved.
 *
 *  `queryKeys.cases.*` and `queryKeys.finance.*` are two separate trees and a
 *  cost line is in both: payables are the vendor-tagged ACTUAL lines net of the
 *  outbound payments allocated to them (`finance/balances.go`), and aging is
 *  computed from the same rows. So
 *  writing one from a case screen — or, since story 6, from the «Провести факт»
 *  button on a direction — moves figures the Money page is showing, and
 *  invalidating the case alone left them as they were.
 *
 *  It is the mirror of `invalidateCase` in `features/finance/api/useFinance.ts`,
 *  which the payment writes call for the same reason in the other direction. */
function invalidateFinance(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
}

/** Amends one folded cost line, both sides of it, in one request.
 *
 *  `lineId` is always the folded line's `id`: the PLAN's id on a planned line,
 *  never its `actualId` — and on an UNPLANNED line (S-4.1), the actual's own
 *  id, which the DTO serves as both `id` and `actualId`. The id of an actual
 *  that realises a plan is a 404 here. So a client holding a folded line
 *  already holds the id these endpoints want, whichever shape it has.
 *
 *  There is no `kind` parameter because there is no side to choose. The whole
 *  draft goes, and the server works out which rows that means: an actual that
 *  arrived for the first time is created against the existing plan, both figures
 *  moving move both rows, and an actual cleared to blank deletes its row and
 *  leaves the plan. Each of those was a defect on this screen while the caller
 *  picked a side and the other side of the draft was dropped without a word. */
export function useAmendExpense(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<ExpenseLine, Error, { lineId: string; draft: ExpenseDraft }>({
    mutationFn: ({ lineId, draft }) =>
      api.put<ExpenseLine>(
        `/cases/${caseId}/expenses/${lineId}`,
        toFoldedExpensePayload(draft),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      invalidateFinance(queryClient)
    },
  })
}

/** One PLANNED cost line as the batch endpoint takes it (issue #35, story 6).
 *
 *  `finance.expenseBody`, not the folded shape the sheet writes: this is the
 *  raw row endpoint, and the batch behind the bulk truck save writes plans and
 *  only plans — six trucks, six agreed rates, none of them invoiced yet.
 *
 *  `kind` is stated rather than defaulted, because the server refuses to guess
 *  it: the difference between a plan and a real cost must not be a matter of
 *  which field a client forgot.
 *
 *  THERE IS NO VENDOR HERE, and its absence is a rule rather than an omission
 *  to fill in later. `ExpenseInput.Validate` refuses a planned line carrying
 *  one (`vendorName: planned-has-no-vendor`) — a vendor belongs to the ACTUAL,
 *  because it is the party who invoiced. Story 6's own wording ("vendor from
 *  the row's carrier") describes the server this endpoint does not have; who
 *  carries the unit is recorded on the UNIT row, which the same save writes
 *  through the leg PUT beside this. */
export interface PlannedExpenseLineInput {
  kind: 'planned'
  item: string
  amount: { amount: string; currency: string }
  /** The direction this rate was agreed for. Required in practice for this
   *  caller although optional on the wire: a unit's rate that named no leg
   *  would be a cost the direction page could never show. */
  legId: string
  /** The unit it was agreed for — any kind (S-18b) — or absent for the leg
   *  rate («Плечо целиком», S-18a). The server refuses it without `legId`
   *  (0056's CHECK), which is why the two travel together here. `unitId`, the
   *  unit on the leg — never the `unit` of measure. The retired `vehicleId`
   *  is refused 422 `retired`. */
  unitId?: string
  /** The generalized plan line this rate details (spec L7), omitted for none.
   *  Six trucks' rates detailing one quote-carried «Transportation service» is
   *  what stops the same movement being planned twice. */
  detailOf?: string
  /** The stored «не детализирует» (S-12): this rate deliberately details no
   *  plan line. Only ever `true`, and never beside `detailOf` — the server
   *  answers 422 `standsAlone: invalid` for the pair. */
  standsAlone?: true
}

/** Writes several planned cost lines in ONE transaction (FR-67, story 6).
 *
 *  The bulk truck save is what posts here: a direction run by six trucks is six
 *  agreed rates entered in one press, and six round trips would leave the case
 *  half-costed whenever one of them failed. All or none — the server's own
 *  words for this endpoint.
 *
 *  Nothing is seeded into the cache. The response carries the STORED rows,
 *  including the FX rate the server resolved and the USD it booked (AD-9,
 *  AD-20), and the figures the direction page shows are derived from them
 *  server-side; writing anything here would be this client inventing money. */
export function useRecordExpenseBatch(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<ExpenseLine[], Error, PlannedExpenseLineInput[]>({
    mutationFn: async (lines) =>
      (
        await api.post<{ items: ExpenseLine[] }>(
          `/cases/${caseId}/expense-lines/batch`,
          { lines },
        )
      ).items,
    onSuccess: () => {
      /* The case detail carries the money block AND — since story 6 — the leg
         page's own composed read, which hangs under the same key prefix. One
         invalidation reaches both, which is the point of having hung it there:
         the rates just written are what the direction's finance card shows. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      invalidateFinance(queryClient)
    },
  })
}

/** The case's cost lines as the RAW-row list serves them.
 *
 *  Read by the direction page for the «Детализирует строку плана» select: the
 *  candidates are the case's unattributed top-level plans, and the leg's own
 *  per-truck rows carry the current answer (spec L7). The case detail's money
 *  block would not do — it is composed for the money screen and a direction
 *  page does not hold it.
 *
 *  Keyed UNDER `cases.detail`, like `legs` and `money` above it, so that every
 *  existing invalidation of the case reaches this list too: the batch that
 *  writes the per-truck rates already invalidates that prefix, and a select
 *  still offering the parent those rates were just filed under is the one thing
 *  this read must not do. */
export function useCaseExpenseLines(caseId: string) {
  return useQuery<ExpenseLine[]>({
    queryKey: [...queryKeys.cases.detail(caseId), 'expense-lines'],
    enabled: caseId !== '',
    queryFn: async () =>
      (await api.get<{ items: ExpenseLine[] }>(`/cases/${caseId}/expense-lines`)).items,
  })
}

/** Re-files one PLANNED line under a generalized parent; `null` detaches it.
 *
 *  `standsAlone: true` beside `detailOf: null` stores the operator's «не
 *  детализирует» (S-12) — only on a per-truck plan, and never beside a parent
 *  (422 `standsAlone: invalid`); a parent clears it server-side, and `null`
 *  without it detaches and clears it.
 *
 *  PATCH and one field, for `useSetTrancheDate`'s reason rather than by
 *  analogy: the figure the money screen holds is the FOLDED planned amount in
 *  the line's booked currency, so a whole-row PUT from a screen that only
 *  changed a grouping would re-book money nobody retyped. This request touches
 *  the link and nothing else.
 *
 *  NOT optimistic. The parent is what the served totals now sum through (spec
 *  L4), so the figures move server-side and the case is what refetches. */
export function useSetExpenseDetailOf(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    ExpenseLine,
    Error,
    { lineId: string; detailOf: string | null; standsAlone?: boolean }
  >({
    mutationFn: ({ lineId, detailOf, standsAlone }) =>
      api.patch<ExpenseLine>(
        `/cases/${caseId}/expense-lines/${lineId}`,
        standsAlone === undefined ? { detailOf } : { detailOf, standsAlone },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      /* The list carries the forecast profit, and a plan leaving the totals is
         one of its two inputs — the same reason the expense writes above
         invalidate it. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      invalidateFinance(queryClient)
    },
  })
}

/** Removes a folded cost line: the plan, and every actual against it.
 *
 *  Takes the folded `id`, like the amend above — the PLAN's id, or on an
 *  unplanned line the actual's own (S-4.1), whose delete removes that one row
 *  and is refused while a payment is allocated to it. Deleting the line the screen shows
 *  means deleting the whole line — the per-row endpoint refuses a plan that has
 *  actuals against it (409) rather than cascading, because deleting it there
 *  would leave those actuals varying against nothing, and this endpoint removes
 *  both sides in one transaction instead. Passing the ACTUAL's id here would
 *  delete the invoice and leave the plan on screen, which is what "delete" used
 *  to do on this screen. */
export function useDropExpense(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: (lineId) =>
      api.delete<void>(`/cases/${caseId}/expenses/${lineId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.list() })
      invalidateFinance(queryClient)
    },
  })
}
